import api from './api'

export type ClienteAgradaRow = {
  id: string
  nome: string
  razaoSocial: string | null
  cnpj: string | null
  email: string | null
  telefone: string | null
  status: string
  createdAt: string
  updatedAt: string
  clienteAtivos: {
    id: string
    status: string
    createdAt: string
    deal: { id: string; nome: string; etapa: string; dataFechamento: string | null } | null
  }[]
  _count: { financeProjects: number; deals: number }
}

export type ClienteAgradaHistoricoItem = {
  id: string
  tipo: 'cadastro' | 'cliente_ativo' | 'deal' | 'projeto' | 'chamado' | 'atividade'
  titulo: string
  detalhe?: string
  data: string
}

export type ClienteAgradaServicoItem = {
  id: string
  descricao: string
  tipo: string
  quantidade: number
  valorUnitario: number | null
  total: number | null
}

export type ClienteAgradaProposalSnapshot = {
  id: string
  versao: number
  totalFinal: number | null
  pdfUrl: string | null
  createdAt: string
  payloadJson: unknown
}

export type ClienteAgradaServicoContratado = {
  id: string
  fonte: 'projeto' | 'cliente_ativo'
  projetoId: string | null
  projetoNome: string | null
  projetoStatus: string | null
  dealId: string | null
  dealNome: string | null
  dataInicio: string | null
  dataFim: string | null
  dataRenovacao: string | null
  plataforma: string | null
  servicosText: string | null
  recorrencia: string | null
  valorMensal: number | null
  valorTotal: number | null
  itens: ClienteAgradaServicoItem[]
  proposalSnapshot: ClienteAgradaProposalSnapshot | null
}

export type ClienteAgradaDetail = ClienteAgradaRow & {
  nomeFantasia: string | null
  contato: string | null
  cargo: string | null
  site: string | null
  segmento: string | null
  plataforma: string | null
  modelo: string | null
  faturamento: string | null
  trafego: string | null
  endereco: string | null
  cep: string | null
  cidade: string | null
  estado: string | null
  responsavelAssinaturaNome: string | null
  responsavelAssinaturaEmail: string | null
  responsavelFinanceiroNome: string | null
  responsavelFinanceiroEmail: string | null
  observacoes: string | null
  parceiro: { id: string; nome: string; empresa: string | null } | null
  contatos: Array<{
    id: string
    nome: string
    email: string | null
    telefone: string | null
    cargo: string | null
    decisor: boolean
    linkedin: string | null
  }>
  clienteAtivos: Array<{
    id: string
    status: string
    createdAt: string
    deal: {
      id: string
      nome: string
      etapa: string
      valor: unknown
      dataFechamento: string | null
      ownerId: string
      updatedAt: string
    } | null
    closer: { id: string; name: string }
  }>
  financeProjects: Array<{
    id: string
    name: string | null
    status: string
    source: string
    createdAt: string
    deal: { id: string; nome: string; etapa: string } | null
    requests: {
      id: string
      protocol: string
      type: string
      status: string
      area: string | null
      createdAt: string
      updatedAt: string
    }[]
  }>
  deals: Array<{
    id: string
    nome: string
    etapa: string
    valor: unknown
    dataFechamento: string | null
    createdAt: string
    updatedAt: string
    owner: { id: string; name: string } | null
  }>
  chamados: {
    id: string
    protocol: string
    type: string
    area: string | null
    status: string
    slaDueAt: string | null
    createdAt: string
    updatedAt: string
  }[]
  historicoFluxos: ClienteAgradaHistoricoItem[]
  servicos: ClienteAgradaServicoContratado[]
}

function normalizeClienteAgradaDetail(data: ClienteAgradaDetail): ClienteAgradaDetail {
  return {
    ...data,
    contatos: data.contatos ?? [],
    clienteAtivos: data.clienteAtivos ?? [],
    financeProjects: data.financeProjects ?? [],
    deals: data.deals ?? [],
    chamados: data.chamados ?? [],
    historicoFluxos: data.historicoFluxos ?? [],
    servicos: (data.servicos ?? []).map((servico) => ({
      ...servico,
      itens: servico.itens ?? [],
      proposalSnapshot: servico.proposalSnapshot ?? null,
    })),
  }
}

export type ClienteVendedorOption = { id: string; name: string; role: string }

export type ContratoAssinaturaPreview = {
  nome: string
  email: string | null
  assinadoEm: string | null
}

export type ClienteManualPayload = {
  nome: string
  cnpj: string
  contatoNome: string
  telefone: string
  responsavelAssinaturaNome: string
  responsavelAssinaturaEmail: string
  responsavelFinanceiroNome: string
  emailFinanceiro: string
  vendedorId: string
  planId: string
  valorMensal: number
  dataFechamento: string
  contractStartDate: string
  firstPaymentDate: string
  contractDeliveryMode: 'pdf' | 'link'
  contractStatus: 'EM_ANDAMENTO' | 'CONTRATO_GERADO_PDF' | 'CONTRATO_ENVIADO' | 'CONTRATO_ASSINADO'
  contractSignatureLink: string
  endereco: string
  servico: string
  observacoes: string
  signedAt?: string
}

export const clientesService = {
  async list(params?: { page?: number; limit?: number; search?: string; filter?: string }) {
    const { data } = await api.get<{ data: ClienteAgradaRow[]; meta: { totalItems: number } }>(
      '/clientes',
      { params },
    )
    return data
  },

  async getById(id: string) {
    const { data } = await api.get<{ data: ClienteAgradaDetail }>(`/clientes/${id}`)
    return normalizeClienteAgradaDetail(data.data)
  },

  async listVendedores() {
    const { data } = await api.get<{ data: ClienteVendedorOption[] }>('/clientes/vendedores')
    return data.data
  },

  async previewContratoAssinado(file: File) {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post<{
      data: {
        assinaturas: ContratoAssinaturaPreview[]
        ultimoAssinadoEm: string | null
      }
    }>('/clientes/preview-contrato-assinado', form)
    return data.data
  },

  async createManual(payload: ClienteManualPayload) {
    const { data } = await api.post<{
      data: { empresaId: string; dealId: string; projectId: string; comissaoCentavos: number | null }
    }>('/clientes', payload)
    return data.data
  },

  async importCsv(file: File) {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post<{
      data: { created: number; updated: number; skipped: number; errors: { line: number; message: string }[] }
    }>('/clientes/import', form)
    return data.data
  },
}
